import { useState, useRef, useCallback } from "react";
import { GiphyGif, SearchResult, RateLimitError } from "../types/giphy";
import { searchGifs, getTrendingGifs, getRandomGifs } from "../lib/giphy";
const GIFS_PER_PAGE = 20;
const MAX_DUPLICATE_RETRY_ATTEMPTS = 3;
const INITIAL_RANDOM_GIFS_COUNT = 3;
interface CacheEntry {
  gifs: GiphyGif[];
  totalCount: number;
  offset: number;
}
interface UseGifSearchState {
  gifs: GiphyGif[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  totalCount: number;
  offset: number;
}
interface UseGifSearchReturn extends UseGifSearchState {
  searchGifs: (query: string, reset?: boolean) => Promise<void>;
  loadMore: () => Promise<void>;
  loadInitialGifs: () => Promise<void>;
}
export function useGifSearch(): UseGifSearchReturn {
  const [state, setState] = useState<UseGifSearchState>({
    gifs: [],
    loading: false,
    error: null,
    hasMore: false,
    totalCount: 0,
    offset: 0
  });

  // Cache by query string
  const cache = useRef<Map<string, CacheEntry>>(new Map());
  const currentQuery = useRef<string>("");
  const loadInitialGifs = useCallback(async () => {
    setState(prev => ({
      ...prev,
      loading: true,
      error: null
    }));
    try {
      let gifs: GiphyGif[];
      try {
        gifs = await getRandomGifs(INITIAL_RANDOM_GIFS_COUNT);
      } catch {
        const result = await getTrendingGifs(INITIAL_RANDOM_GIFS_COUNT);
        gifs = result.gifs;
      }
      setState(prev_1 => ({
        ...prev_1,
        gifs,
        loading: false,
        hasMore: false,
        totalCount: gifs.length,
        offset: 0
      }));
    } catch (error) {
      setState(prev_0 => ({
        ...prev_0,
        loading: false,
        error: error instanceof Error ? error.message : "useGifSearch error "
      }));
    }
  }, []);
  const performSearch = useCallback(async (query: string, reset: boolean = true) => {
    const cacheKey = query || "__trending__";
    const isNewSearch = reset;
    setState(prev_2 => ({
      ...prev_2,
      loading: true,
      error: null,
      ...(isNewSearch && {
        gifs: []
      })
    }));
    try {
      let searchOffset = 0;
      let currentGifs: GiphyGif[] = [];
      setState(prev_7 => {
        searchOffset = isNewSearch ? 0 : prev_7.offset;
        currentGifs = prev_7.gifs;
        return prev_7;
      });
      let result_0: SearchResult;
      let uniqueGifs: GiphyGif[] = [];
      let attempts = 0;

      // duplicate fix
      while (attempts < MAX_DUPLICATE_RETRY_ATTEMPTS && uniqueGifs.length === 0) {
        if (!query) {
          result_0 = await getTrendingGifs(GIFS_PER_PAGE);
        } else {
          result_0 = await searchGifs(query, searchOffset, GIFS_PER_PAGE);
        }
        if (isNewSearch) {
          uniqueGifs = result_0.gifs;
        } else {
          const existingIds = new Set(currentGifs.map(gif => gif.id));
          uniqueGifs = result_0.gifs.filter(gif_0 => !existingIds.has(gif_0.id));
        }
        if (!isNewSearch && uniqueGifs.length === 0 && result_0.gifs.length > 0) {
          attempts++;
          searchOffset += GIFS_PER_PAGE;
        } else {
          break;
        }
      }
      const newGifs = isNewSearch ? uniqueGifs : [...currentGifs, ...uniqueGifs];
      const hasMore = result_0!.gifs.length === GIFS_PER_PAGE && uniqueGifs.length > 0 && newGifs.length < result_0!.totalCount;
      const newOffset = result_0!.offset + result_0!.gifs.length;
      if (isNewSearch) {
        cache.current.set(cacheKey, {
          gifs: newGifs,
          totalCount: result_0!.totalCount,
          offset: newOffset
        });
      } else {
        cache.current.set(cacheKey, {
          gifs: newGifs,
          totalCount: result_0!.totalCount,
          offset: newOffset
        });
      }
      setState(prev_8 => ({
        ...prev_8,
        gifs: newGifs,
        loading: false,
        hasMore,
        totalCount: result_0!.totalCount,
        offset: newOffset
      }));
      currentQuery.current = query;
    } catch (error_0) {
      if (error_0 instanceof RateLimitError) {
        const cachedResult = cache.current.get(cacheKey);
        if (cachedResult) {
          if (isNewSearch) {
            setState(prev_3 => ({
              ...prev_3,
              gifs: cachedResult.gifs,
              loading: false,
              hasMore: false,
              totalCount: cachedResult.totalCount,
              offset: cachedResult.offset,
              error: null
            }));
          } else {
            setState(prev_4 => ({
              ...prev_4,
              loading: false,
              hasMore: false,
              error: null
            }));
          }
          return;
        }

        // If no cache available thne show error
        setState(prev_5 => ({
          ...prev_5,
          loading: false,
          hasMore: false,
          error: "Rate limit exceeded. Please try again later."
        }));
      } else {
        setState(prev_6 => ({
          ...prev_6,
          loading: false,
          hasMore: false,
          error: error_0 instanceof Error ? `Search error: ${error_0.message}` : "An error occurred while searching"
        }));
      }
    }
  }, []);
  const searchGifsCallback = useCallback(async (query_0: string, reset_0: boolean = true) => {
    await performSearch(query_0, reset_0);
  }, [performSearch]);
  const loadMore = useCallback(async () => {
    if (state.loading || !state.hasMore) return;
    await performSearch(currentQuery.current, false);
  }, [performSearch, state.loading, state.hasMore]);
  return {
    ...state,
    searchGifs: searchGifsCallback,
    loadMore,
    loadInitialGifs
  };
}